import { FormEvent, useEffect, useRef, useState } from "react";
import {
  WEB_SCAN_PORTS,
  WebScanError,
  isLoopbackHostname,
  normalizePrivateSubnet,
  requestLocalNetworkAccess,
  scanWebNetwork,
  type WebScanProgress,
  type WebScanResult,
} from "./webNetworkScanner";

type ScanState =
  | "idle"
  | "configure"
  | "paywall"
  | "permission"
  | "scanning"
  | "complete"
  | "error";

const workflow = [
  {
    number: "01",
    title: "Connectez-vous",
    copy: "Lancez le contrôle depuis le Wi-Fi du logement, avec votre accord explicite.",
  },
  {
    number: "02",
    title: "Testez les services web",
    copy: "Le navigateur vérifie si des services HTTP(S) répondent sur le réseau privé choisi.",
  },
  {
    number: "03",
    title: "Inspectez la pièce",
    copy: "L’app iPhone complétera ce premier signal par une découverte native et une checklist physique.",
  },
  {
    number: "04",
    title: "Comprenez le résultat",
    copy: "Chaque observation reste séparée des limites et de la couverture réelle du contrôle.",
  },
];

const commonSubnets = ["192.168.1", "192.168.0", "10.0.0"];

const emptyProgress: WebScanProgress = {
  phase: "probing",
  completed: 0,
  total: 254 * WEB_SCAN_PORTS.length,
  percent: 0,
  currentHost: "",
  responsiveServices: 0,
};

function pluralize(value: number, singular: string, plural = `${singular}s`) {
  return value === 1 ? singular : plural;
}

function confidenceLabel(confidence: "none" | "low" | "medium") {
  if (confidence === "medium") return "Confiance modérée";
  if (confidence === "low") return "Confiance faible";
  return "Type indéterminé";
}

export function App() {
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [subnetInput, setSubnetInput] = useState("192.168.1");
  const [hasNetworkConsent, setHasNetworkConsent] = useState(false);
  const [configurationError, setConfigurationError] = useState("");
  const [scanProgress, setScanProgress] = useState<WebScanProgress>(emptyProgress);
  const [scanResult, setScanResult] = useState<WebScanResult | null>(null);
  const [scanError, setScanError] = useState("");
  const scanRef = useRef<HTMLElement>(null);
  const scanAbortRef = useRef<AbortController | null>(null);
  const stateHeadingRef = useRef<HTMLHeadingElement>(null);

  const normalizedSubnet = normalizePrivateSubnet(subnetInput);

  useEffect(() => () => scanAbortRef.current?.abort(), []);

  useEffect(() => {
    if (scanState !== "idle") stateHeadingRef.current?.focus();
  }, [scanState]);

  const openScan = () => {
    if (scanState === "idle") setScanState("configure");
    scanRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const openConfiguration = () => {
    scanAbortRef.current?.abort();
    scanAbortRef.current = null;
    setScanResult(null);
    setScanError("");
    setConfigurationError("");
    setHasNetworkConsent(false);
    setScanProgress(emptyProgress);
    setScanState("configure");
  };

  const reviewPurchase = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subnet = normalizePrivateSubnet(subnetInput);
    if (!subnet) {
      setConfigurationError(
        "Saisissez un préfixe IPv4 privé /24, par exemple 192.168.1. Les réseaux publics ne sont pas autorisés.",
      );
      return;
    }
    if (!hasNetworkConsent) {
      setConfigurationError("Confirmez que vous êtes autorisé à tester ce réseau avant de continuer.");
      return;
    }

    setSubnetInput(subnet);
    setConfigurationError("");
    setScanState("paywall");
  };

  const startScan = async () => {
    if (scanAbortRef.current) return;
    const subnet = normalizePrivateSubnet(subnetInput);
    if (!subnet || !hasNetworkConsent) {
      openConfiguration();
      return;
    }
    if (!window.isSecureContext) {
      setScanError("L’accès au réseau local nécessite une connexion HTTPS sécurisée.");
      setScanState("error");
      return;
    }
    if (typeof window.fetch !== "function" || typeof window.AbortController !== "function") {
      setScanError("Ce navigateur ne prend pas en charge ce scan expérimental. Utilisez une version récente de Chrome.");
      setScanState("error");
      return;
    }

    const controller = new AbortController();
    const requiresPermission = !isLoopbackHostname(window.location.hostname);
    scanAbortRef.current = controller;
    setScanResult(null);
    setScanError("");
    setScanProgress(emptyProgress);
    setScanState(requiresPermission ? "permission" : "scanning");

    try {
      if (requiresPermission) {
        await requestLocalNetworkAccess({ subnet, signal: controller.signal });
        if (scanAbortRef.current !== controller) return;
        setScanState("scanning");
      }

      const result = await scanWebNetwork({
        subnet,
        signal: controller.signal,
        onProgress: (nextProgress) => {
          if (scanAbortRef.current !== controller) return;
          setScanProgress(nextProgress);
          setScanState("scanning");
        },
      });
      if (scanAbortRef.current !== controller) return;

      setScanResult(result);
      setScanState("complete");
    } catch (error) {
      if (error instanceof WebScanError && error.code === "aborted") {
        if (scanAbortRef.current === controller) openConfiguration();
        return;
      }
      setScanError(
        error instanceof WebScanError && error.code === "permission-denied"
          ? "L’accès au réseau local est refusé. Autorisez SAN Privacy dans les paramètres Chrome du site, puis réessayez."
          : error instanceof WebScanError
            ? error.message
            : "Le scan n’a pas pu aboutir. Vérifiez le Wi-Fi et les autorisations du navigateur, puis réessayez.",
      );
      setScanState("error");
    } finally {
      if (scanAbortRef.current === controller) scanAbortRef.current = null;
    }
  };

  const stopScan = () => {
    scanAbortRef.current?.abort();
    openConfiguration();
  };

  const visibleHosts = scanResult?.responsiveHosts.slice(0, 12) ?? [];
  const hiddenHostCount = Math.max(0, (scanResult?.responsiveHosts.length ?? 0) - visibleHosts.length);

  const scanAnnouncement =
    scanState === "configure"
      ? "Configuration du réseau à tester."
      : scanState === "paywall"
        ? "Paywall fictif affiché avant le scan."
        : scanState === "permission"
          ? "En attente de l’autorisation réseau local de Chrome."
          : scanState === "scanning"
            ? "Scan des services web en cours."
            : scanState === "complete"
              ? "Scan terminé. Résultats disponibles."
              : scanState === "error"
                ? "Le scan n’a pas pu être terminé."
                : "Scan prêt à être configuré.";

  return (
    <main>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="SAN Privacy, accueil">
          <span className="brand-mark" aria-hidden="true">S</span>
          <span>SAN Privacy</span>
        </a>
        <nav aria-label="Navigation principale">
          <a href="#fonctionnement">Fonctionnement</a>
          <a href="#limites">Limites</a>
          <button className="nav-cta" onClick={openScan}>Lancer un scan</button>
        </nav>
      </header>

      <section className="hero" id="top">
        <div className="hero-copy">
          <div className="eyebrow"><span /> Scan web expérimental · app iPhone bientôt</div>
          <h1>Votre chambre mérite mieux qu’un doute.</h1>
          <p className="hero-lead">
            SAN Privacy teste les services web visibles sur le Wi-Fi, explique ce que ces
            observations signifient et vous guide dans l’inspection de la pièce.
          </p>
          <div className="hero-actions">
            <button className="primary-button" onClick={openScan}>Lancer un scan</button>
            <a className="text-link" href="#fonctionnement">Comment ça marche <span>↘</span></a>
          </div>
          <p className="trust-line">Sans accès aux flux vidéo. Sans conclusion alarmiste. Sans promesse impossible.</p>
        </div>

        <div className="hero-visual" aria-label="Aperçu du contrôle SAN Privacy">
          <div className="signal-orbit signal-orbit-one" />
          <div className="signal-orbit signal-orbit-two" />
          <div className="phone-card">
            <div className="phone-topline">
              <span className="phone-pill">Contrôle actif</span>
              <span>02:14</span>
            </div>
            <div className="radar">
              <div className="radar-ring radar-ring-large" />
              <div className="radar-ring radar-ring-small" />
              <div className="radar-sweep" />
              <div className="radar-core"><span /></div>
              <span className="device-dot dot-one" />
              <span className="device-dot dot-two" />
              <span className="device-dot dot-three" />
            </div>
            <div className="phone-summary">
              <span>7 services web observés</span>
              <strong>Analyse en cours</strong>
            </div>
          </div>
          <div className="floating-note note-one"><span /> Analyse locale</div>
          <div className="floating-note note-two"><span /> Résultat expliqué</div>
        </div>
      </section>

      <section className="principles" aria-label="Principes du produit">
        <p>Calme, précis, protecteur.</p>
        <div>
          <span>Requêtes lancées depuis cet appareil</span>
          <span>Aucun accès aux flux</span>
          <span>Limites toujours visibles</span>
        </div>
      </section>

      <section className="workflow section-shell" id="fonctionnement">
        <div className="section-heading">
          <p className="section-kicker">Le parcours</p>
          <h2>Un contrôle guidé.<br />Pas un écran opaque.</h2>
          <p>Le scan web fournit un premier signal de joignabilité. L’app iPhone ajoutera ensuite les capacités réseau natives.</p>
        </div>
        <div className="workflow-grid">
          {workflow.map((step) => (
            <article className="workflow-card" key={step.number}>
              <span className="step-number">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="scan-section section-shell" ref={scanRef}>
        <div className="scan-copy">
          <p className="section-kicker">Résultat explicable</p>
          <h2>Des observations réelles. Jamais une fausse certitude.</h2>
          <p>
            Le scan tente de joindre quatre ports HTTP(S) sur un réseau privé /24 depuis
            votre navigateur. Une réponse peut venir d’un routeur, d’une TV, d’une imprimante,
            d’une caméra ou d’un autre équipement.
          </p>
          <div className="coverage-legend">
            <div><span className="legend-dot observation" /> Services web joignables</div>
            <div><span className="legend-dot coverage" /> Couverture technique limitée</div>
          </div>
        </div>

        <article className="scan-card">
          <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{scanAnnouncement}</p>
          <div className="scan-header">
            <div>
              <span className="scan-label">Scan web expérimental · Chrome</span>
              <h3>Contrôle du réseau local</h3>
            </div>
            <span className={`status-light ${scanState}`} aria-hidden="true" />
          </div>

          {scanState === "idle" && (
            <div className="scan-idle">
              <div className="idle-glyph"><span /><span /><span /></div>
              <p>Testez la joignabilité de services HTTP(S) sur le Wi-Fi auquel cet appareil est connecté.</p>
              <button className="primary-button full" onClick={() => setScanState("configure")}>Configurer le scan</button>
            </div>
          )}

          {scanState === "configure" && (
            <form className="scan-setup" onSubmit={reviewPurchase} noValidate>
              <h4 ref={stateHeadingRef} tabIndex={-1}>Choisissez le réseau privé à tester</h4>
              <p>
                SAN Privacy testera la joignabilité des services web puis tentera d’analyser
                une courte réponse uniquement lorsque l’appareil l’autorise via CORS. Aucun
                flux vidéo ni mot de passe n’est consulté.
              </p>
              <label htmlFor="subnet-prefix">Préfixe IPv4 privé</label>
              <div className="subnet-input-row">
                <input
                  id="subnet-prefix"
                  name="subnet-prefix"
                  value={subnetInput}
                  onChange={(event) => {
                    setSubnetInput(event.target.value);
                    setHasNetworkConsent(false);
                    setConfigurationError("");
                  }}
                  placeholder="192.168.1"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-describedby={configurationError ? "subnet-help subnet-error" : "subnet-help"}
                  aria-invalid={Boolean(configurationError && !normalizedSubnet)}
                />
                <span>.1–254</span>
              </div>
              <p className="field-help" id="subnet-help">
                Trois premiers nombres uniquement. Le scan est limité à un réseau privé /24.
              </p>
              <div className="subnet-presets" aria-label="Préfixes courants">
                {commonSubnets.map((subnet) => (
                  <button
                    type="button"
                    className={subnetInput === subnet ? "selected" : ""}
                    key={subnet}
                    onClick={() => {
                      setSubnetInput(subnet);
                      setHasNetworkConsent(false);
                      setConfigurationError("");
                    }}
                  >
                    {subnet}
                  </button>
                ))}
              </div>
              <div className="scan-scope">
                <strong>Portée du contrôle</strong>
                <span>254 adresses · ports 80, 443, 8080 et 8443</span>
              </div>
              <label className="consent-row">
                <input
                  type="checkbox"
                  checked={hasNetworkConsent}
                  onChange={(event) => {
                    setHasNetworkConsent(event.target.checked);
                    setConfigurationError("");
                  }}
                />
                <span>
                  Je confirme être connecté à ce réseau et être autorisé à tester la
                  joignabilité de ses services web.
                </span>
              </label>
              {configurationError && <p className="form-error" id="subnet-error" role="alert">{configurationError}</p>}
              <button className="primary-button full" type="submit">
                Continuer
              </button>
            </form>
          )}

          {scanState === "paywall" && (
            <div className="paywall" role="region" aria-labelledby="paywall-title">
              <div className="paywall-topline">
                <span>PASS CONTRÔLE</span>
                <strong>4,99 €</strong>
              </div>
              <h4 id="paywall-title" ref={stateHeadingRef} tabIndex={-1}>Débloquez ce contrôle expérimental.</h4>
              <p>Vous allez tester les services web joignables sur {subnetInput}.1–254 depuis ce navigateur.</p>
              <ul>
                <li><span>✓</span> Test des ports web courants</li>
                <li><span>✓</span> Identification opportuniste des réponses lisibles</li>
                <li><span>✓</span> Exécution depuis cet appareil</li>
                <li><span>✓</span> Portée et limites détaillées</li>
              </ul>
              <button className="primary-button full" onClick={startScan}>Débloquer et demander l’accès</button>
              <button className="paywall-back" onClick={() => setScanState("configure")}>Modifier le réseau</button>
              <p className="paywall-note">Paiement fictif — aucune carte ne sera demandée et aucun débit ne sera effectué.</p>
            </div>
          )}

          {scanState === "permission" && (
            <div className="permission-state">
              <div className="permission-icon" aria-hidden="true">↗</div>
              <h4 ref={stateHeadingRef} tabIndex={-1}>Autorisation Chrome requise</h4>
              <p>
                Chrome peut demander l’autorisation d’accéder aux appareils du réseau local.
                Acceptez pour envoyer les tests HTTP(S) au préfixe {subnetInput}.
              </p>
              <span>Répondez à la demande du navigateur pour continuer.</span>
              <button className="secondary-button" onClick={stopScan}>Annuler</button>
            </div>
          )}

          {scanState === "scanning" && (
            <div className="scan-progress">
              <h4 className="visually-hidden" ref={stateHeadingRef} tabIndex={-1}>Scan des services web en cours</h4>
              <div className="progress-meta">
                <span>
                  {scanProgress.phase === "probing"
                    ? "Test des services web en cours"
                    : "Analyse des réponses lisibles"}
                </span>
                <strong>{scanProgress.percent}%</strong>
              </div>
              <div
                className="progress-track"
                role="progressbar"
                aria-label="Progression du scan"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={scanProgress.percent}
              >
                <span style={{ width: `${scanProgress.percent}%` }} />
              </div>
              <div className="scan-live-meta">
                <strong>
                  {scanProgress.completed} sur {scanProgress.total}{" "}
                  {scanProgress.phase === "probing"
                    ? "tests HTTP(S)"
                    : pluralize(scanProgress.total, "service observé analysé", "services observés analysés")}
                </strong>
                <span>{scanProgress.responsiveServices} {pluralize(scanProgress.responsiveServices, "service web joignable", "services web joignables")}</span>
                {scanProgress.currentHost && <span>Adresse en cours : {scanProgress.currentHost}</span>}
              </div>
              <p className="scan-safety-note">
                HTTP(S) uniquement. SAN lit au maximum les 32 premiers Ko d’une page si CORS
                l’autorise. Aucun flux vidéo et aucun identifiant ne sont consultés.
              </p>
              <button className="secondary-button" onClick={stopScan}>Interrompre le scan</button>
            </div>
          )}

          {scanState === "complete" && scanResult && (
            <div className="scan-result">
              <h4 className="visually-hidden" ref={stateHeadingRef} tabIndex={-1}>Résultats du scan</h4>
              <div className={`result-block ${scanResult.responsiveServices > 0 ? "observation-result" : "coverage-result"}`}>
                <span>Observation réseau</span>
                <strong>
                  {scanResult.responsiveServices > 0
                    ? `${scanResult.responsiveServices} ${pluralize(scanResult.responsiveServices, "service web joignable observé", "services web joignables observés")}`
                    : "Aucun service web joignable observé"}
                </strong>
                <p>
                  {scanResult.responsiveServices > 0
                    ? scanResult.readableServices > 0
                      ? `${scanResult.readableServices} ${pluralize(scanResult.readableServices, "réponse était lisible", "réponses étaient lisibles")} via CORS et ont été analysées à la recherche d’indices explicables.`
                      : "Les connexions ont abouti, mais leurs réponses restent opaques : Chrome masque leurs en-têtes et leur contenu au code de la page."
                    : "Le contrôle reste non concluant : Chrome, un certificat local, l’isolation Wi-Fi ou le réseau peuvent avoir bloqué les tests."}
                </p>
              </div>

              {visibleHosts.length > 0 && (
                <div className="device-list" aria-label="Services web joignables observés">
                  {visibleHosts.map((host) => (
                    <div className="device-row" key={host.ip}>
                      <div className="device-summary">
                        <div className="device-title-row">
                          <strong>{host.classification.label}</strong>
                          <span className={`confidence-badge ${host.classification.confidence}`}>
                            {confidenceLabel(host.classification.confidence)}
                          </span>
                        </div>
                        <code>{host.ip}</code>
                        <span>
                          {host.classification.readableServices > 0
                            ? `${host.classification.readableServices}/${host.services.length} ${pluralize(host.services.length, "réponse lisible", "réponses lisibles")}`
                            : "Réponse opaque pour l’application web"}
                        </span>
                        {host.classification.signals.length > 0 && (
                          <ul className="evidence-list" aria-label={`Indices pour ${host.ip}`}>
                            {host.classification.signals.map((signal) => (
                              <li key={signal}>{signal}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="port-list">
                        {host.services.map((service) => (
                          <span className="port-pill" key={`${host.ip}-${service.port}`}>
                            {service.protocol.toUpperCase()} :{service.port}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {hiddenHostCount > 0 && <p className="more-results">+ {hiddenHostCount} autres adresses observées</p>}
                </div>
              )}

              {scanResult.responsiveServices > 0 && (
                <div className="result-block analysis-result">
                  <span>Identification opportuniste</span>
                  <strong>
                    {scanResult.classifiedHosts > 0
                      ? `${scanResult.classifiedHosts} ${pluralize(scanResult.classifiedHosts, "type probable proposé", "types probables proposés")}`
                      : "Aucun type déterminable depuis le navigateur"}
                  </strong>
                  <p>
                    Une réponse joignable n’est analysable que si l’appareil autorise CORS.
                    Les autres services restent listés sans type inventé.
                  </p>
                </div>
              )}

              <div className="result-block coverage-result">
                <span>Couverture très partielle</span>
                <strong>{scanResult.attemptedHosts} adresses · {scanResult.attemptedServices} tests HTTP(S)</strong>
                <p>
                  Ports 80, 443, 8080 et 8443 uniquement. HTTPS n’est observable que si le
                  certificat est accepté par Chrome. Ce scan ne couvre ni ARP, ni mDNS, ni SSDP,
                  ni ONVIF, ni RTSP et ne prouve jamais l’absence ou la présence d’une caméra.
                </p>
              </div>
              <div className="result-actions">
                <button className="primary-button full" onClick={() => setScanState("paywall")}>Relancer</button>
                <button className="secondary-button" onClick={openConfiguration}>Scanner un autre préfixe</button>
              </div>
            </div>
          )}

          {scanState === "error" && (
            <div className="scan-error" role="alert">
              <span>Scan non lancé ou non concluant</span>
              <h4 ref={stateHeadingRef} tabIndex={-1}>Le navigateur n’a pas pu terminer le contrôle.</h4>
              <p>{scanError}</p>
              <button className="primary-button full" onClick={openConfiguration}>Vérifier la configuration</button>
            </div>
          )}

          <p className="scan-disclaimer">
            Scan expérimental : requêtes HTTP(S) lancées localement depuis cet appareil. Seules
            les courtes réponses explicitement autorisées par CORS sont analysées ; aucun flux
            vidéo n’est consulté.
          </p>
        </article>
      </section>

      <section className="limits section-shell" id="limites">
        <div className="limits-number">0%</div>
        <div className="limits-copy">
          <p className="section-kicker">La limite fondamentale</p>
          <h2>L’absence d’indice ne signifie pas l’absence de caméra.</h2>
          <p>
            Une caméra peut être hors ligne, enregistrer sur carte mémoire, utiliser un autre réseau ou la 4G,
            ou rester invisible derrière l’isolation Wi-Fi. SAN Privacy est un assistant d’inspection,
            jamais une certification du lieu.
          </p>
        </div>
      </section>

      <section className="privacy section-shell">
        <div className="privacy-badge" aria-hidden="true"><span>Local</span></div>
        <div>
          <p className="section-kicker">Confidentialité</p>
          <h2>Votre inspection concerne votre vie privée. Elle doit le rester.</h2>
          <p>Les résultats restent dans cet onglet ; seules les requêtes de test sont envoyées aux adresses locales choisies. Aucune donnée n’est transmise au backend de SAN.</p>
        </div>
      </section>

      <section className="closing section-shell">
        <p className="section-kicker">Première étape</p>
        <h2>Testez le premier contrôle web sur le terrain.</h2>
        <p>L’app iPhone complétera ce signal limité avec la découverte réseau native et une checklist physique claire.</p>
        <button className="primary-button" onClick={openScan}>Lancer un scan</button>
      </section>

      <footer>
        <a className="brand" href="#top"><span className="brand-mark">S</span><span>SAN Privacy</span></a>
        <p>Contrôle de confidentialité pour voyageurs.</p>
        <p>© 2026 SAN</p>
      </footer>
    </main>
  );
}
